import type { AuthConfigNanoId } from '@composio/types'
import { useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/css'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import { cloneDeep } from 'lodash'
import { CodeIcon, CopyIcon, PlusIcon } from 'lucide-react'

import { useApiKey } from '~/dataProcessor/hooks/user'
import { Switch } from '~/design-system/atom/switch'
import { useToast } from '~/design-system/atom/use-toast'
import { patchAuthConfigsByNanoidByStatus } from '~/generated/client'
import {
  useGetTriggerData,
  useGetTriggerInstanceData,
} from '~/hooks/query/triggers'
import { useRouterParsed } from '~/hooks/utils/useRouterParse'
import { useGetConnectorInfo } from '~/pages/connector/[connectorId]'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { IdDropDown } from '~/ui/components/base/idDropDown'
import { LoadingState } from '~/ui/containers/base/emptyStates'
import { CodeWInstructionModal } from '~/ui/containers/codeModal'
import LogsScreen from '~/ui/screens/action_logs/logs'
import { ActionRenderer } from '~/ui/screens/actions'
import { SetupFormWizard } from '~/ui/screens/appScreen/modules/createIntegration/CreateIntegrationPopover'
import { TriggerRenderer } from '~/ui/screens/triggers'
import { showFirstAndLastNLetters } from '~/utils/string'
import { TabBarStrip } from '../../components/custom/TabBarStrip'
import { RightSideContent } from '../../containers/base/base'
import { AddAccountButton } from '../../containers/common/AddAccountButton'
import { ConnectedAccountsRenderer } from '../connectedAccountList'
import { DeleteConnectorAction } from '../integrations/index'
import McpServerScreen from '../mcp'
import { TriggerInstanceRenderer } from '../triggers/triggerPage'

export default function IntegrationPage() {
  const { authConfigId } = useRouterParsed()

  const { data: connectorInfo } = useGetConnectorInfo(authConfigId)

  const [sections, setSections] = useState<string[]>([
    'connected accounts',
    'actions',
    'triggers',
    'trigger instances',
    'logs',
    'settings',
    'MCP',
  ])

  const { triggerData, isLoading: isTriggerLoading } = useGetTriggerData(
    connectorInfo?.toolkit.slug!,
  )
  const {
    triggerData: triggerInstanceData,
    isLoading: isTriggerInstanceLoading,
  } = useGetTriggerInstanceData()
  const [showNewMCPWizard, setShowNewMCPWizard] = useState(false)

  useEffect(() => {
    if (!isTriggerLoading && !triggerData?.items?.length) {
      setSections((prev) => prev.filter((section) => section !== 'triggers'))
    }

    if (!isTriggerInstanceLoading && !triggerInstanceData?.items?.length) {
      setSections((prev) =>
        prev.filter((section) => section !== 'trigger instances'),
      )
    }
  }, [isTriggerLoading, isTriggerInstanceLoading])

  const toast = useToast()

  const [isChecked, setIsChecked] = useState<boolean>(
    connectorInfo?.status === 'ENABLED' || false,
  )

  useEffect(() => {
    setIsChecked(connectorInfo?.status === 'ENABLED' || false)
  }, [connectorInfo])

  const [selectedSection, setSelectedSection] = useState(sections[0])

  // const isLoading = !connectorInfo || isTriggerLoading || isTriggerInstanceLoading;
  const isLoading = !connectorInfo

  const { mutate: setIsActiveStatus } = useMutation({
    mutationFn: ({
      nanoid,
      isEnabled,
    }: {
      nanoid: AuthConfigNanoId
      isEnabled: boolean
    }) => {
      return patchAuthConfigsByNanoidByStatus({
        nanoid: nanoid,
        status: isEnabled ? 'ENABLED' : 'DISABLED',
      })
    },
    onSuccess: () => {
      toast.toast({
        title: 'Status updated',
        description: `Connector ${isChecked ? 'enabled' : 'disabled'}`,
      })
    },
    onError: () => {
      setIsChecked(!isChecked)
      toast.toast({
        title: 'Error',
        description: `Error in updating status`,
        variant: 'destructive',
      })
    },
  })

  return (
    <RightSideContent
      header={
        !!connectorInfo
          ? `${connectorInfo?.toolkit.slug?.charAt(0)?.toUpperCase() + connectorInfo?.toolkit.slug?.slice(1)} live tool`
          : 'Fetching live tool'
      }
    >
      {isLoading ? (
        <div
          className={css`
            height: calc(100vh - 200px);
          `}
        >
          <LoadingState height={window.innerHeight - 200} />
        </div>
      ) : (
        connectorInfo && (
          <div className="h-full pt-4">
            <div className="flex w-full items-center justify-between px-4">
              <div className="flex items-center gap-3 ">
                <div
                  className={clsx(
                    'flex h-9 w-9 items-center justify-center',
                    css`
                      background-image: url('${connectorInfo?.toolkit.logo}');

                      background-position: center;
                      background-repeat: no-repeat;
                      background-size: cover;
                    `,
                  )}
                ></div>
                <div className="ml-2">
                  <div className="mb-1  text-[16px] font-[500]  text-black-400">
                    {connectorInfo?.name}
                  </div>
                  <div className="flex gap-[12px]  text-[#000]">
                    <IdDropDown
                      ids={[
                        {
                          key: 'v1/v2',
                          label: 'v1/v2 (uuid)',
                          id: connectorInfo?.uuid,
                        },
                        {
                          key: 'v3',
                          label: 'v3 (nanoid)',
                          id: connectorInfo?.id,
                        },
                      ]}
                    >
                      <span className="flex cursor-pointer items-center gap-2">
                        {showFirstAndLastNLetters(connectorInfo?.id || '', 5)}
                        <CopyIcon height={12} width={12} strokeWidth={1.4} />
                      </span>
                    </IdDropDown>
                    {/* <CopyBlock text={connectorInfo?.id || ""} label="V3 ID" /> */}
                  </div>
                </div>
              </div>
              <div
                className="flex items-center gap-3"
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                <ShowIntegrationCode integrationId={connectorInfo?.id} />
                {!!connectorInfo ? (
                  <AddAccountButton
                    connectorData={connectorInfo}
                    isMountedAtTop={true}
                  />
                ) : null}
                <DeleteConnectorAction
                  connectorId={connectorInfo?.id}
                  toolSlug={connectorInfo?.toolkit.slug || ''}
                />
                <Switch
                  checked={isChecked}
                  id="airplane-mode"
                  onCheckedChange={(e) => {
                    setIsChecked(e)
                    if (connectorInfo?.id) {
                      setIsActiveStatus({
                        nanoid: connectorInfo?.id,
                        isEnabled: e,
                      })
                    }
                  }}
                />
              </div>
            </div>
            <div className="relative mt-8 flex w-full items-center text-[16px] tracking-[.35px] text-[#e0e0e0]">
              <TabBarStrip
                sections={sections}
                selectedSection={selectedSection}
                setSelectedSection={setSelectedSection}
              />
              {selectedSection === 'MCP' && (
                <div className="absolute right-4">
                  <Button
                    onClick={() => setShowNewMCPWizard(true)}
                    variant="secondary_outline"
                    uplifted={false}
                    size="24"
                  >
                    <PlusIcon size={14} />
                    Add MCP Server
                  </Button>
                </div>
              )}
            </div>
            {selectedSection === 'connected accounts' && (
              <div className={clsx(' px-4 pt-2', bottomTabElementCSS)}>
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <ConnectedAccountsRenderer />
                </ErrorBoundary>
              </div>
            )}
            {selectedSection === 'actions' && (
              <div className={clsx(' px-4', bottomTabElementCSS)}>
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <ActionRenderer />
                </ErrorBoundary>
              </div>
            )}
            {selectedSection === 'triggers' && (
              <div className={clsx('px-4 pt-2', bottomTabElementCSS)}>
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <TriggerRenderer />
                </ErrorBoundary>
              </div>
            )}

            {selectedSection === 'trigger instances' && (
              <div className={clsx('pt- px-4', bottomTabElementCSS)}>
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <TriggerInstanceRenderer />
                </ErrorBoundary>
              </div>
            )}
            {selectedSection === 'logs' && (
              <div className={clsx('pt-1', bottomTabElementCSS)}>
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <LogsScreen
                    type="action"
                    searchParams={
                      connectorInfo?.id
                        ? [
                            {
                              operation: '==',
                              field: 'integration_id',
                              value: connectorInfo.id,
                            },
                          ]
                        : []
                    }
                  />
                </ErrorBoundary>
              </div>
            )}
            {selectedSection === 'settings' && (
              <div className="ml-2 mt-6 max-w-[550px] px-4">
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <SetupFormWizard
                    appKey={connectorInfo?.toolkit.slug || ''}
                    initAuthConfig={connectorInfo?.credentials ?? {}}
                    authConfigId={connectorInfo?.id}
                    initAuthSchemeType={connectorInfo?.auth_scheme}
                    viaSettingsPage={true}
                    limitedActions={connectorInfo?.restrict_to_following_tools}
                  />
                </ErrorBoundary>
              </div>
            )}
            {selectedSection === 'MCP' && (
              <div className="ml-2 mt-6 px-4">
                <ErrorBoundary
                  fallback={
                    <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                  }
                >
                  <McpServerScreen
                    appName={connectorInfo?.toolkit.slug}
                    showNewWizard={showNewMCPWizard}
                    setShowNewWizard={setShowNewMCPWizard}
                    integrationId={connectorInfo.uuid}
                    connectorInfo={connectorInfo}
                  />
                </ErrorBoundary>
              </div>
            )}
          </div>
        )
      )}
    </RightSideContent>
  )
}

const bottomTabElementCSS = css`
  .minHeightWrapper {
    height: calc(100vh - 400px);
  }
`

export const sampleCodeBlockData = {
  content: {
    python: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for creating a connection for a user',
          blocks: [
            {
              heading: 'Get integration',
              text: `from composio import ComposioToolSet, App

# Example usage
toolset = ComposioToolSet(api_key={{api_key}})
integration_id = {{integration_id}}
integration = toolset.get_integration(id=integration_id)
print(integration) # Integration object
`,
              language: 'python',
            },
          ],
        },
      },
    ],
    javascript: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Get integration',
          blocks: [
            {
              heading: 'Get integration',
              text: `import {Composio} from "composio-core";
// get composio client
const composio = new Composio({apiKey: {{api_key}}});

const integration = await composio.integrations.get({
    integrationId: {{integration_id}}
})
`,
              language: 'javascript',
            },
          ],
        },
      },
    ],
  },
}

const ShowIntegrationCode = ({ integrationId }: { integrationId: string }) => {
  const [showCode, setShowCode] = useState(false)

  const apiKey = useApiKey()

  const transformedData = useMemo(() => {
    // transform data of sampleCodeBlockData
    const data = cloneDeep(sampleCodeBlockData)

    for (const language of Object.keys(data.content)) {
      // @ts-expect-error - this is okay for now
      for (const languageItem of data.content[language]) {
        //replace blocks with api key and integration id
        const { blocks } = languageItem.content

        if (!blocks) continue

        for (const block of blocks) {
          block.text = block.text
            .replace('{{api_key}}', `"${apiKey}"`)
            .replace('{{integration_id}}', `"${integrationId}"`)
        }
      }
    }

    return data
  }, [apiKey, integrationId])

  return (
    <div>
      <CodeButton setShowCode={setShowCode} />
      {showCode && (
        <CodeWInstructionModal
          title="Get integration with Our SDKs"
          data={transformedData}
          setOpen={() => setShowCode(false)}
        />
      )}
    </div>
  )
}

export const CodeButton = ({
  setShowCode,
  text = 'Code',
}: {
  setShowCode: (value: boolean) => void
  text?: string
  variant?: 'white' | 'black'
}) => {
  return (
    <Button
      size="36"
      variant="secondary"
      onClick={() => {
        setShowCode(true)
      }}
      className="text-black gap-1 !px-[10px]"
    >
      {text.includes('Add') ? (
        <PlusIcon height={16} width={16} strokeWidth={2.2} color={'#fff'} />
      ) : (
        <CodeIcon height={16} width={16} strokeWidth={2.2} color={'#fff'} />
      )}
      <span className="ml-1 mt-[2px] text-[13px] font-[500]">{text}</span>
    </Button>
  )
}
